import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, MessageCircle } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import { getLocalizedPath } from '@/config/routes';

const content = {
  nl: {
    eyebrow: 'Vraag snel een offerte aan',
    title: 'Klaar voor een website die echt klanten oplevert?',
    subtitle: 'Vertel ons wat je nodig hebt via het contactformulier of rechtstreeks via de chatbot. We bekijken je project en bezorgen je een duidelijke prijsinschatting.',
    ctaPrimary: 'Vraag een gratis offerte aan',
    ctaSecondary: 'Open de chatbot'
  },
  en: {
    eyebrow: 'Request a quote quickly',
    title: 'Ready for a website that actually generates customers?',
    subtitle: 'Tell us what you need via the contact form or directly via the chatbot. We\'ll review your project and provide you with a clear price estimate.',
    ctaPrimary: 'Request a free quote',
    ctaSecondary: 'Open the chatbot'
  },
  fr: {
    eyebrow: 'Demandez un devis rapidement',
    title: 'Prêt pour un site web qui génère vraiment des clients?',
    subtitle: 'Dites-nous ce dont vous avez besoin via le formulaire de contact ou directement via le chatbot.',
    ctaPrimary: 'Demander un devis gratuit',
    ctaSecondary: 'Ouvrir le chatbot'
  },
  de: {
    eyebrow: 'Fordern Sie schnell ein Angebot an',
    title: 'Bereit für eine Website, die wirklich Kunden generiert?',
    subtitle: 'Sagen Sie uns, was Sie brauchen, über das Kontaktformular oder direkt über den Chatbot.',
    ctaPrimary: 'Kostenloses Angebot anfordern',
    ctaSecondary: 'Chatbot öffnen'
  }
};

export const CTASection = () => {
  const { language } = useLanguage();
  const t = content[language] || content.nl;
  const contactPath = getLocalizedPath('contact', language);

  const openChatbot = () => {
    // Try to open the chatbot
    const chatbotBtn = document.querySelector('[data-testid="chatbot-toggle-btn"]');
    if (chatbotBtn) chatbotBtn.click();
  };

  return (
    <section 
      className="py-20 sm:py-28"
      data-testid="cta-section"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative text-center py-12 sm:py-16 px-6 sm:px-12 rounded-3xl overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
            boxShadow: '0 20px 50px rgba(37, 99, 235, 0.25)'
          }}
        >
          {/* Background decoration */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-0 left-1/4 w-64 h-64 bg-white/5 rounded-full blur-3xl" />
            <div className="absolute bottom-0 right-1/4 w-48 h-48 bg-blue-400/10 rounded-full blur-2xl" />
          </div>

          <div className="relative z-10">
            <span 
              className="inline-block px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wider mb-6"
              style={{
                background: 'rgba(255, 255, 255, 0.15)',
                color: '#ffffff'
              }}
            >
              {t.eyebrow}
            </span>
            
            <h2 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-extrabold text-white mb-6 leading-tight">
              {t.title}
            </h2>
            
            <p className="text-lg text-white/80 mb-10 max-w-2xl mx-auto leading-relaxed">
              {t.subtitle}
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to={contactPath}
                className="group inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold text-[#2563EB] bg-white transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1"
                data-testid="cta-primary"
              >
                {t.ctaPrimary}
                <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <button
                onClick={openChatbot}
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold text-white border-2 border-white/50 hover:border-white hover:bg-white/10 transition-all duration-300"
                data-testid="cta-secondary"
              >
                <MessageCircle size={20} />
                {t.ctaSecondary}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
